import { Col, CollapseProps, Flex, Modal, Row, Table, TableProps } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

// components
import ActionListHeaderComponent from 'modules/main/actions/listHeader';
import TableTotal from 'components/tableTotal/tableTotal';
import NoData from 'components/noData/noData';
import StatusComponent from 'components/status/status';
import ListButton from 'components/listButton/listButton';

// services
import { onCancelMeeting, onGetMeetingsForActiveBoard, onStartMeetingAttendance, onStartMeetingVoting } from '../services/meetingServices';
import NotificationService from 'core/services/notification';
import { onGetServiceCost } from '../../../commonServices';
import { StatusColors } from 'utils/statusColors';
import { isRTL } from 'core/services/language';
import { dateFormat } from 'utils/dateFormat';

// models
import { ServiceCost } from '../../../commonModels';
import { Meeting } from '../models/meetings';
import ConfirmModalComponent from 'components/confirmModal/confirmModal.component';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import InfiniteScroll from 'react-infinite-scroll-component';
import { onDeleteBoardDocument, onGetBoardDocumentById, onGetBoardDocumentsList } from '../services/documentsServices';
import { BoardDocument } from '../models/documents';
import { use } from 'i18next';
import Icon from 'utils/icon';
import DisabledElement from 'components/disabledElement/disabledElement';
import FilePreview from 'components/filePreview/filePreview';
import { formatFileSize } from 'utils/FileSize';

// hooks
import useCompanySuspended from 'core/hooks/useCompanySuspended';

const panelStyle: React.CSSProperties = {
	padding: '10px 20px',
	marginBottom: 24,
	background: '#fff',
	borderRadius: 'var(--radius)',
	border: 'none',
};

interface BoardDocumentsListPageProps {}

const BoardDocumentsListPage: FunctionComponent<BoardDocumentsListPageProps> = () => {
	const { t } = useTranslation();

	const { suspendedProps } = useCompanySuspended();

	const navigate = useNavigate();
	const location = useLocation();

	const [isListLoading, setIsListLoading] = useState<boolean>(true);
	const [documents, setDocuments] = useState<Omit<BoardDocument, 'files'>[]>([]);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalRecords, setTotalRecords] = useState<number>(0);
	const [isActiveBoardExist, setIsActiveBoardExist] = useState<boolean>(false);

	const [documentDetails, setDocumentDetails] = useState<BoardDocument>();
	const [isDocumentLoading, setIsDocumentLoading] = useState<boolean>(false);
	const [isDetailsModalOpen, setIsDetailsModalOpen] = useState<boolean>(false);

	const [tempDeletedDocumentId, setTempDeletedDocumentId] = useState<number | null>(null);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
	const [isDeleting, setIsDeleting] = useState<boolean>(false);

	const columns: TableProps<Omit<BoardDocument, 'files'>>['columns'] = [
		{
			title: t('COMMON.ID'),
			dataIndex: 'id',
			key: 'id',
		},
		{
			title: t('ACTIONS.TITLE'),
			dataIndex: 'translated_title',
			key: 'title',
			render: (translated_title) => <span>{isRTL() ? translated_title.ar : translated_title.en}</span>,
		},
		{
			title: t('COMMON.DESCRIPTION'),
			dataIndex: 'translated_description',
			key: 'description',
			render: (translated_description) => <span>{isRTL() ? translated_description.ar : translated_description.en}</span>,
		},
		{
			title: t('ACTIONS.FILES_COUNT'),
			dataIndex: 'files_count',
			key: 'files_count',
		},
		{
			title: t('COMMON.CREATED_AT'),
			dataIndex: 'created_at',
			key: 'created_at',
			render: (created_at) => <span>{dateFormat(created_at)}</span>,
		},
		{
			title: t('COMMON.ACTIONS'),
			dataIndex: '_',
			key: '_',
			render: (_, row) => (
				<Flex gap={10} justify="center">
					<ListButton
						type="view"
						text={t('COMMON.VIEW')}
						onClick={() => {
							getDocumentHandler(row.id);
							setIsDetailsModalOpen(true);
						}}
					/>

					<ListButton type="edit" {...suspendedProps} onClick={() => navigate(`/dashboard/actions/boards/documents/${row.id}`)} />

					<ListButton
						type="delete"
						{...suspendedProps}
						onClick={() => {
							setTempDeletedDocumentId(row.id);
							setIsDeleteModalOpen(true);
						}}
					/>
				</Flex>
			),
		},
	];

	useEffect(() => {
		getBoardDocuments();
	}, []);

	const getBoardDocuments = async () => {
		try {
			setIsListLoading(true);

			const response = await onGetBoardDocumentsList(1);
			setDocuments(response.documents);

			setTotalRecords(response.pagination.total);

			setIsActiveBoardExist(true);
			setIsListLoading(false);
		} catch (error: any) {
			console.log(error);
			if (error?.response?.data?.message === 'No active board found for this company') {
				setIsListLoading(false);
				setIsActiveBoardExist(false);
				return;
			}

			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			setIsListLoading(false);
		}
	};

	const fetchMoreData = async () => {
		try {
			setIsListLoading(true);

			const response = await onGetBoardDocumentsList(currentPage + 1);
			setDocuments(response.documents);

			setCurrentPage(currentPage + 1);
			setIsListLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			setIsListLoading(false);
		}
	};

	const getDocumentHandler = async (id: number) => {
		try {
			setIsDocumentLoading(true);

			const response = await onGetBoardDocumentById(`${id}`);

			setDocumentDetails(response);

			setIsDocumentLoading(false);
		} catch (error) {
			setIsDocumentLoading(false);
			setIsDetailsModalOpen(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const deleteDocumentHandler = async () => {
		try {
			if (!tempDeletedDocumentId) return;

			setIsDeleting(true);

			await onDeleteBoardDocument(tempDeletedDocumentId);

			getBoardDocuments();

			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.DOCUMENT_DELETED_SUCCESSFULLY'));

			setIsDeleting(false);
			setIsDeleteModalOpen(false);
			setTempDeletedDocumentId(null);
		} catch (error) {
			setIsDeleting(false);
			NotificationService.error(t('COMMON.ERROR'), t('ACTIONS.ERROR_DELETING_DOCUMENT'));
		}
	};

	return (
		<>
			<ActionListHeaderComponent
				title={t('ACTIONS.BOARD_DOCUMENTS')}
				subTitle={''}
				newRequestText={t('ACTIONS.NEW_DOCUMENT')}
				addAvailable={isActiveBoardExist}
				addAvailableText={t('ACTIONS.ADD_ACTIVE_BOARD_TO_ADD_DOCUMENT')}
				costLoading={isListLoading}
			/>

			<Table
				columns={columns}
				dataSource={documents}
				className="bordered"
				scroll={{ x: 600 }}
				rowKey="id"
				loading={isListLoading}
				locale={{ emptyText: <NoData /> }}
				pagination={{
					pageSize: 15,
					total: totalRecords,
					showSizeChanger: false,
					current: currentPage,
					onChange: fetchMoreData,
					showTotal: (total) => <TableTotal total={total} />,
				}}
			/>

			{/* details modal  */}
			<Modal
				width={{
					sm: '90%',
					md: '80%',
					lg: '80%',
					xl: '50%',
					xxl: '40%',
				}}
				open={isDetailsModalOpen}
				title={
					<Flex gap={10} align="center">
						<Icon name="info" width={30} height={30} />
						<h6 className="mb-0">{t('ACTIONS.DOCUMENT_DETAILS')}</h6>
					</Flex>
				}
				footer={[]}
				onCancel={() => {
					setIsDetailsModalOpen(false);
					setDocumentDetails(undefined);
				}}>
				{isDocumentLoading ? (
					<LoadingComponent />
				) : (
					<Row gutter={[16, 16]} className="mt-4">
						<Col span={24} md={12}>
							<DisabledElement label={t('ACTIONS.TITLE_IN_AR')} value={documentDetails?.translated_title.ar} />
						</Col>
						<Col span={24} md={12}>
							<DisabledElement label={t('ACTIONS.TITLE_IN_EN')} value={documentDetails?.translated_title.en} />
						</Col>
						<Col span={24} md={12}>
							<DisabledElement label={t('ACTIONS.DESCRIPTION_IN_ARABIC')} value={documentDetails?.translated_description.ar} />
						</Col>
						<Col span={24} md={12}>
							<DisabledElement label={t('ACTIONS.DESCRIPTION_IN_ENGLISH')} value={documentDetails?.translated_description.en} />
						</Col>
						<Col span={24}>
							{documentDetails?.files.map((file) => (
								<FilePreview
									key={file.id}
									name={file.title}
									downloadLink={file.download_link}
									data={formatFileSize(file.size_in_kilobyte)}
									className="mb-3"
								/>
							))}
						</Col>
					</Row>
				)}
			</Modal>

			{/* delete modal */}
			<ConfirmModalComponent
				open={isDeleteModalOpen}
				confirmText={t('COMMON.DELETE')}
				confirmColor="danger"
				onConfirm={deleteDocumentHandler}
				onDiscard={() => setIsDeleteModalOpen(false)}
				title={t('ACTIONS.DELETE_DOCUMENT')}
				description={t('ACTIONS.CONFIRM_DELETE_DOCUMENT')}
				isLoading={isDeleting}
			/>
		</>
	);
};

export default BoardDocumentsListPage;
